'use client'

import { useCallback, useEffect, useState } from 'react'
import { usePathname, useParams } from 'next/navigation'
import { createTinyUrl } from '@/core/tiny-url/api'

interface UseShareUrlOptions {
  useTinyUrl?: boolean
}

interface UseShareUrlReturn {
  url: string
  displayUrl: string
  isLoading: boolean
  error: string | null
}

export const useShareUrl = ({ useTinyUrl = false }: UseShareUrlOptions = {}): UseShareUrlReturn => {
  const pathname = usePathname()
  const params = useParams()
  const [url, setUrl] = useState('')
  const [displayUrl, setDisplayUrl] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generateTinyUrl = useCallback(
    async (fullUrl: string) => {
      try {
        setIsLoading(true)
        setError(null)

        let slug: string | undefined
        if (params.slug) {
          slug = params.slug as string
        }

        const shortUrl = await createTinyUrl(fullUrl, slug)
        if (shortUrl) {
          setDisplayUrl(shortUrl)
        } else {
          setError('Failed to create short URL')
          setDisplayUrl(fullUrl)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create short URL')
        setDisplayUrl(fullUrl)
      } finally {
        setIsLoading(false)
      }
    },
    [params.slug]
  )

  useEffect(() => {
    const fullUrl = useTinyUrl ? window.location.href : `${window.location.origin}${pathname}`

    setUrl(fullUrl)

    if (useTinyUrl) {
      void generateTinyUrl(fullUrl)
    } else {
      setDisplayUrl(fullUrl)
    }
  }, [pathname, useTinyUrl, generateTinyUrl])

  return {
    url,
    displayUrl,
    isLoading,
    error
  }
}
